'use client'

import { useState } from 'react'
import { Activity, CalendarClock, Globe, Play, Plus, Server } from 'lucide-react'
import { PageFrame, Module, ModuleFooter, StatusPill, Tag, Segmented, Fields, EmptyState, Progress, Tabs, Toolbar, SearchInput, AttentionNote, useNotice } from '../ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { PlatformMark } from '../shell/PlatformMark'
import { ResizableTable, TableTd } from '../ResizableTable'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// Every primitive of the design system on one editor-only page
// (/entries/ui-kit), so a change to a token or a primitive can be eyeballed
// in both themes without opening six dashboards. Not a product page.

export function UiKitView({ theme }: { theme: ProcessedTheme }) {
  const [seg, setSeg] = useState<'secure' | 'elevated' | 'under_threat'>('secure')
  const [multi, setMulti] = useState<string[]>(['running'])
  const [tab, setTab] = useState<'a' | 'b' | 'c'>('a')
  const [on, setOn] = useState(true)
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState(false)
  const { notify } = useNotice()

  return (
    <PageFrame
      eyebrow="Administration"
      title="UI kit"
      info={<p>Every primitive of docs/ui-harmony-plan.md C3, rendered once. Toggle the theme in the topbar to check both.</p>}
      primaryAction={<Button size="lg" onClick={() => setDialog(true)}><Play className="h-3.5 w-3.5" /> Run a job</Button>}
      headerActions={<Button variant="outline" onClick={() => notify('Schedule saved in the preview.', { tone: 'success' })}>Notice</Button>}
      metrics={[
        { label: 'Workers online', value: 5 },
        { label: 'Running now', value: 3, tone: 'signal' },
        { label: 'Queue', value: 0 },
        { label: 'Browser handoffs', value: 0, note: '25 records' },
        { label: 'Failed · 24h', value: 8, tone: 'danger' },
      ]}
    >
      <Module title="Status pills and tags" icon={Activity} summary="tones">
        <div className="flex flex-wrap items-center gap-3 px-5 py-5">
          <StatusPill tone="success">Online</StatusPill>
          <StatusPill tone="signal">Running</StatusPill>
          <StatusPill tone="warning">Queued</StatusPill>
          <StatusPill tone="danger">Stalled</StatusPill>
          <StatusPill tone="neutral">Cancelled</StatusPill>
          <Tag>auto_domains.analyze_5g</Tag>
          <Tag mono={false}>Shared template</Tag>
          <PlatformMark code="ctt" /><PlatformMark code="mbway" /><PlatformMark code="viaverde" /><PlatformMark code="mbcp" /><PlatformMark code="sixt" /><PlatformMark code="edp" label="EDP" /><PlatformMark code="ctt" size="md" />
        </div>
      </Module>

      <Module title="Controls" icon={Server} summary="buttons · inputs · toggle · segmented · tabs">
        <div className="flex flex-col gap-5 px-5 py-5">
          <div className="flex flex-wrap items-center gap-2">
            <Button>Primary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Delete</Button>
            <Button variant="action"><Plus className="h-3 w-3" /> Add</Button>
            <Button variant="action" size="xs">Run now</Button>
            <Button size="icon" variant="ghost"><Play /></Button>
          </div>
          <div className="grid max-w-2xl grid-cols-2 gap-3">
            <Input placeholder="Search all schedule fields…" />
            <select className="h-9 rounded-[4px] border border-border bg-card px-2.5 font-mono text-label text-foreground"><option>All</option><option>Enabled</option><option>Disabled</option></select>
            <Textarea placeholder="scope=monitor" className="col-span-2 font-mono text-label" />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 font-mono text-label text-muted-foreground"><Switch checked={on} onCheckedChange={setOn} /> {on ? 'Enabled' : 'Disabled'}</label>
            <Segmented value={seg} onChange={setSeg} segments={[{ value: 'secure', label: 'Secure' }, { value: 'elevated', label: 'Elevated' }, { value: 'under_threat', label: 'Under threat' }]} />
            <Segmented size="sm" multi value={multi} onChange={(v) => setMulti((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]))} segments={[{ value: 'queued', label: 'Queued', count: 0 }, { value: 'running', label: 'Running', count: 3 }, { value: 'failed', label: 'Failed', count: 8 }]} />
          </div>
          <Tabs value={tab} onChange={setTab} tabs={[{ value: 'a', label: 'Campaigns', count: 12 }, { value: 'b', label: 'Entries', count: 140 }, { value: 'c', label: 'Alerts' }]} />
          <SearchInput value={search} onChange={setSearch} placeholder="Search handoffs…" />
        </div>
      </Module>

      <Module title="Running now" icon={Activity} summary="2 running · 0 queued" actions={<Button variant="action" size="xs">Refresh</Button>}>
        <div className="relative border-b border-border px-5 py-5 before:absolute before:bottom-5 before:left-0 before:top-5 before:w-[2px] before:bg-signal">
          <div className="flex items-center gap-3">
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2"><strong className="text-body font-medium">Domain analysis · 5G</strong><span className="font-mono text-micro text-muted-foreground">viaverde</span><StatusPill tone="success">Running</StatusPill></span>
            <span className="font-mono text-micro text-muted-foreground">1h 7m</span>
          </div>
          <Progress value={198} max={200} label="198/200" className="mt-4" />
        </div>
        <div className="relative bg-danger-surface px-5 py-5 before:absolute before:bottom-5 before:left-0 before:top-5 before:w-[2px] before:bg-destructive">
          <div className="flex items-center gap-3">
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2"><strong className="text-body font-medium">Domain analysis · 5G</strong><span className="font-mono text-micro text-muted-foreground">ctt</span><StatusPill tone="danger">Stalled</StatusPill></span>
            <span className="font-mono text-micro text-muted-foreground">4h 13m</span>
          </div>
          <Progress value={61} max={200} tone="danger" label="61/200" className="mt-4" />
          <div className="-mx-5 -mb-5 mt-4 border-t border-border bg-background px-5 py-3">
            <Fields columns={2} fields={[{ label: 'Job', value: <code className="font-mono text-label">auto_domains.analyze_5g</code> }, { label: 'Brand', value: 'ctt' }, { label: 'Started by', value: 'schedule' }, { label: 'Worker', value: 'apple.laptop.lan:5g' }, { label: 'Parameters', pre: true, value: 'name_code=ctt\nscope=pending\nlimit=200' }]} />
            <div className="mt-4 flex gap-2"><Button variant="action" size="xs">Cancel job</Button></div>
          </div>
        </div>
      </Module>

      <Module title="Table" icon={Globe} summary="0 open · 3 records" defaultCollapsed={false}>
        <Toolbar end={<select className="h-8 rounded-[2px] border border-border bg-background px-2 font-mono text-label"><option>All</option></select>}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search handoffs…" />
        </Toolbar>
        <ResizableTable theme={theme} maximizable={false} columns={[{ key: 'status', label: 'Status' }, { key: 'job', label: 'Job' }, { key: 'brand', label: 'Brand' }, { key: 'age', label: 'Age' }]}>
          {[['fulfilled', 'success'], ['failed', 'danger'], ['pending', 'warning']].map(([s, t]) => (
            <tr key={s} className="border-b border-border hover:bg-secondary">
              <TableTd><StatusPill tone={t as 'success'}>{s}</StatusPill></TableTd>
              <TableTd className="font-mono text-label">auto_domains.source</TableTd>
              <TableTd className="font-mono text-label">ctt</TableTd>
              <TableTd className="text-subtle">2h ago</TableTd>
            </tr>
          ))}
        </ResizableTable>
        <ModuleFooter>3 records · 0 waiting for a browser</ModuleFooter>
      </Module>

      <Module title="Empty state and notes" icon={CalendarClock}>
        <EmptyState icon={CalendarClock} title="No schedules yet" action={<Button variant="outline"><Plus className="h-3 w-3" /> Add schedule</Button>}>Workers apply changes on their next heartbeat.</EmptyState>
        <div className="flex flex-col gap-3 px-5 pb-5"><AttentionNote>Review the flagged assets and confirm any authorised partners.</AttentionNote><AttentionNote tone="danger">worker stopped reporting; no heartbeat for 00:05:00</AttentionNote></div>
      </Module>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader><DialogTitle>Run a job</DialogTitle><DialogDescription>Manual run · preview only.</DialogDescription></DialogHeader>
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5 text-nav font-medium">Job<select className="h-10 rounded-[4px] border border-border bg-background px-2.5 text-nav font-normal"><option>Domain analysis · 5G</option></select></label>
            <label className="flex flex-col gap-1.5 text-nav font-medium">Parameters<Textarea defaultValue="scope=monitor" className="font-mono text-label" /></label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={() => { setDialog(false); notify('Manual run added.', { tone: 'success' }) }}><Play className="h-3.5 w-3.5" /> Start job</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageFrame>
  )
}
